/**
 * 复盘告警时间线：按环节分组展示整场断言告警（含命中片段与纠错口径）。
 */
import { sideLabel } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState } from '../ui/primitives.tsx';
import { SEVERITY_LABEL, formatTimeOfDay } from '../../export/report.ts';

export function ReviewAlertTimeline() {
  const { state } = useArena();
  const byStage = new Map<number, typeof state.alerts>();
  for (const alert of state.alerts) {
    const list = byStage.get(alert.stageIndex) ?? [];
    list.push(alert);
    byStage.set(alert.stageIndex, list);
  }
  const stages = [...byStage.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <section className="rounded-xl border border-white/10 bg-bg-surface/80 backdrop-blur">
      <header className="flex items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">断言告警时间线</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            按环节回放 · 共 {state.alerts.length} 条
          </p>
        </div>
        <Badge tone={state.alerts.length > 0 ? 'violation' : 'neutral'}>
          {state.alerts.filter((alert) => alert.severity === 'VIOLATION').length} 违规
        </Badge>
      </header>
      <div className="p-3">
        {state.alerts.length === 0 ? (
          <EmptyState title="未发现口径错误" detail="本场没有触发 forbiddenMisquotes 规则。" />
        ) : (
          <ol className="space-y-2">
            {stages.map(([stageIndex, alerts]) => (
              <li key={stageIndex}>
                <div className="mb-1 flex items-center gap-2">
                  <span className="mono rounded bg-double-blind/15 px-1.5 py-0.5 text-[11px] text-double-blind">
                    环节 {stageIndex}
                  </span>
                  <span className="text-[10px] text-slate-600">{alerts.length} 条</span>
                </div>
                <ul className="space-y-1">
                  {[...alerts]
                    .sort((a, b) => a.at - b.at)
                    .map((alert) => (
                      <li
                        key={alert.id}
                        data-testid="review-alert-item"
                        className="rounded-lg border border-assert-violation/25 bg-assert-violation/[0.06] px-2.5 py-1.5"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-1">
                          <div className="flex items-center gap-1.5">
                            <Badge tone={alert.severity === 'VIOLATION' ? 'violation' : 'warning'}>
                              {SEVERITY_LABEL[alert.severity] ?? alert.severity}
                            </Badge>
                            <span
                              className={
                                alert.side === 'AFF' ? 'text-[11px] text-team-aff' : 'text-[11px] text-team-neg'
                              }
                            >
                              {sideLabel(alert.side)}
                            </span>
                          </div>
                          <span className="mono text-[10px] text-slate-600">
                            {formatTimeOfDay(alert.at)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs font-medium text-assert-violation">
                          {alert.message}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-400">
                          命中原文：{alert.snippet}
                          {alert.matchedText && (
                            <span className="ml-1 rounded bg-assert-violation/20 px-1 text-assert-violation">
                              {alert.matchedText}
                            </span>
                          )}
                        </p>
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
